import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DAYS, getMondayISO, shiftWeek, formatWeekRange, formatKg } from "@/lib/menu-utils";
import { ChevronLeft, ChevronRight, Save, X, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const NONE = "__none__";

export default function Menus() {
  const { can } = useAuth();
  const canWrite = can("menus:write");
  const [companies, setCompanies] = useState([]);
  const [proteins, setProteins] = useState([]);
  const [preparations, setPreparations] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [weekStart, setWeekStart] = useState(getMondayISO());
  const [menu, setMenu] = useState(null);
  const [days, setDays] = useState(() => DAYS.map(d => ({ day: d.key, preparation1_id: null, preparation2_id: null })));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadBase = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: p }, { data: pr }] = await Promise.all([
        api.get("/companies"), api.get("/proteins"), api.get("/preparations")
      ]);
      setCompanies(c); setProteins(p); setPreparations(pr);
      if (!companyId && c.length) setCompanyId(c[0].id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };

  const loadMenu = async () => {
    if (!companyId || !weekStart) return;
    try {
      const { data } = await api.get("/menus", { params: { company_id: companyId, week_start: weekStart } });
      const m = data[0];
      setMenu(m || null);
      const byDay = {};
      (m?.days || []).forEach(d => { byDay[d.day] = d; });
      setDays(DAYS.map(d => ({
        day: d.key,
        preparation1_id: byDay[d.key]?.preparation1_id || null,
        preparation2_id: byDay[d.key]?.preparation2_id || null,
      })));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  useEffect(() => { loadBase(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMenu(); /* eslint-disable-next-line */ }, [companyId, weekStart]);

  const prepById = useMemo(() => Object.fromEntries(preparations.map(p => [p.id, p])), [preparations]);
  const proteinById = useMemo(() => Object.fromEntries(proteins.map(p => [p.id, p])), [proteins]);
  const company = companies.find(c => c.id === companyId);
  const meals = company?.meals_per_day || 0;

  const prepLabel = (prep) => {
    const pr = proteinById[prep.protein_id];
    return `${pr?.name || "?"} — ${prep.name} · ${prep.grams_per_meal}g`;
  };

  const kgFor = (prep) => prep && meals ? ((prep.grams_per_meal * meals) / 1000) : 0;

  const setSlot = (dayKey, slot, prepId) => {
    setDays(ds => ds.map(d => d.day === dayKey ? { ...d, [`preparation${slot}_id`]: prepId === NONE ? null : prepId } : d));
  };

  const save = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      await api.post("/menus", { company_id: companyId, week_start: weekStart, days });
      toast.success("Cardápio salvo");
      loadMenu();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const duplicatePrev = async () => {
    if (!companyId) return;
    const prev = shiftWeek(weekStart, -1);
    try {
      await api.post("/menus/duplicate", { company_id: companyId, source_week_start: prev, target_week_start: weekStart });
      toast.success("Cardápio da semana anterior duplicado");
      loadMenu();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const weekTotalKg = useMemo(() => {
    let t = 0;
    for (const d of days) {
      const p1 = prepById[d.preparation1_id];
      const p2 = prepById[d.preparation2_id];
      t += kgFor(p1) + kgFor(p2);
    }
    return t;
  }, [days, prepById, meals]);

  if (loading) return <div className="p-8 text-slate-500 text-sm">Carregando…</div>;

  if (companies.length === 0) {
    return (
      <div data-testid="menus-page">
        <PageHeader title="Cardápios Semanais" />
        <Section>
          <EmptyState
            title="Cadastre uma empresa primeiro"
            description="É preciso ter ao menos uma empresa cadastrada para montar o cardápio."
            action={<Link to="/empresas"><Button className="bg-emerald-900 hover:bg-emerald-800 text-white">Ir para Empresas</Button></Link>}
          />
        </Section>
      </div>
    );
  }
  if (preparations.length === 0) {
    return (
      <div data-testid="menus-page">
        <PageHeader title="Cardápios Semanais" />
        <Section>
          <EmptyState
            title="Cadastre proteínas e preparações"
            description="É preciso ter preparações cadastradas para selecionar no cardápio."
            action={<Link to="/proteinas"><Button className="bg-emerald-900 hover:bg-emerald-800 text-white">Ir para Proteínas</Button></Link>}
          />
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="menus-page">
      <PageHeader
        title="Cardápios Semanais"
        subtitle="Selecione duas opções de proteína por dia — os kg são calculados automaticamente."
        actions={
          canWrite ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={duplicatePrev} data-testid="duplicate-prev-week-btn"><Copy size={16} className="mr-1"/> Duplicar semana anterior</Button>
              <Button onClick={save} disabled={saving} data-testid="save-menu-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                <Save size={16} className="mr-1" /> Salvar cardápio
              </Button>
            </div>
          ) : (
            <span className="text-xs text-slate-500 italic">Somente leitura</span>
          )
        }
      />

      <div className="grid md:grid-cols-3 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <Label className="mp-kpi-label">Empresa</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="mt-1 h-9" data-testid="menu-company-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.meals_per_day} ref./dia)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <Label className="mp-kpi-label">Semana</Label>
          <div className="flex items-center gap-1 mt-1">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} data-testid="menu-week-prev"><ChevronLeft size={16} /></Button>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="h-9" data-testid="menu-week-input" />
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} data-testid="menu-week-next"><ChevronRight size={16} /></Button>
          </div>
          <div className="text-xs text-slate-500 mt-1">{formatWeekRange(weekStart)}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">Total da semana</div>
          <div className="mp-kpi-value">{formatKg(weekTotalKg)} kg</div>
          <div className="mp-kpi-sub">{meals} refeições/dia × 5-7 dias</div>
        </div>
      </div>

      <Section title={`Cardápio — ${company?.name || ""}`}>
        <div className="overflow-x-auto">
          <table className="mp-table w-full" data-testid="menu-editor-table">
            <thead>
              <tr>
                <th className="w-32">Dia</th>
                <th>Opção 1</th>
                <th className="mp-num w-24">Kg 1</th>
                <th>Opção 2</th>
                <th className="mp-num w-24">Kg 2</th>
                <th className="mp-num w-28">Total dia</th>
              </tr>
            </thead>
            <tbody>
              {DAYS.map((d) => {
                const day = days.find(x => x.day === d.key);
                const p1 = prepById[day?.preparation1_id];
                const p2 = prepById[day?.preparation2_id];
                const kg1 = kgFor(p1); const kg2 = kgFor(p2);
                return (
                  <tr key={d.key} data-testid={`menu-day-${d.key}`}>
                    <td className="font-medium text-slate-800">{d.label}</td>
                    <td>
                      <PrepSelect value={day?.preparation1_id} onChange={(v) => setSlot(d.key, 1, v)} preparations={preparations} prepLabel={prepLabel} testid={`prep1-${d.key}`} />
                    </td>
                    <td className="mp-num tabular-nums text-slate-700">{kg1 ? formatKg(kg1) : "—"}</td>
                    <td>
                      <PrepSelect value={day?.preparation2_id} onChange={(v) => setSlot(d.key, 2, v)} preparations={preparations} prepLabel={prepLabel} testid={`prep2-${d.key}`} />
                    </td>
                    <td className="mp-num tabular-nums text-slate-700">{kg2 ? formatKg(kg2) : "—"}</td>
                    <td className="mp-num tabular-nums font-medium text-slate-900">{(kg1 + kg2) ? formatKg(kg1 + kg2) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {menu && <div className="text-xs text-slate-500 mt-3">Última atualização: {new Date(menu.updated_at).toLocaleString("pt-BR")}</div>}
    </div>
  );
}

function Label({ children, className = "" }) {
  return <div className={`text-xs uppercase tracking-wide text-slate-500 font-semibold ${className}`}>{children}</div>;
}

function PrepSelect({ value, onChange, preparations, prepLabel, testid }) {
  return (
    <div className="flex items-center gap-1">
      <Select value={value || NONE} onValueChange={onChange}>
        <SelectTrigger className="h-9 min-w-[220px]" data-testid={testid}><SelectValue placeholder="Selecionar preparação" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— nenhum —</SelectItem>
          {preparations.map(pr => <SelectItem key={pr.id} value={pr.id}>{prepLabel(pr)}</SelectItem>)}
        </SelectContent>
      </Select>
      {value && (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onChange(NONE)} data-testid={`${testid}-clear`}><X size={14} /></Button>
      )}
    </div>
  );
}